import { AnimatePresence, useReducedMotion } from 'motion/react'
import sumBy from 'lodash/sumBy'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'
import { useCartStoreOrNull } from 'stores/cartStore/CartStoreProvider'
import * as S from './CartCountBadge.styles'

const MAX_DISPLAYED_COUNT = 99

const ENTER_TRANSITION = {
  type: 'spring',
  stiffness: 520,
  damping: 14,
  mass: 0.8,
} as const

const COUNT_TRANSITION = {
  type: 'spring',
  stiffness: 600,
  damping: 30,
} as const

export default function CartCountBadge() {
  // The cart store is only constructed on the client, so SSR renders no badge.
  const cartStore = useCartStoreOrNull()
  const itemsCount = useMobxSelector(
    () => (cartStore ? sumBy(cartStore.cartItems, (item) => item.quantity) : 0),
    [cartStore]
  )
  const shouldReduceMotion = useReducedMotion() ?? false

  const displayedCount =
    itemsCount > MAX_DISPLAYED_COUNT ? `${MAX_DISPLAYED_COUNT}+` : itemsCount

  return (
    <AnimatePresence>
      {itemsCount > 0 && (
        <S.Badge
          data-testid="cart-count-badge"
          initial={
            shouldReduceMotion
              ? { opacity: 0 }
              : { scale: 0, rotate: -45, y: 6, opacity: 0 }
          }
          animate={{ scale: 1, rotate: 0, y: 0, opacity: 1 }}
          exit={{ scale: 0, opacity: 0, transition: { duration: 0.15 } }}
          transition={ENTER_TRANSITION}
        >
          <S.Bubble>
            <S.CountClip>
              <AnimatePresence mode="popLayout" initial={false}>
                <S.Count
                  key={displayedCount}
                  initial={shouldReduceMotion ? false : { y: 12, scale: 0.6 }}
                  animate={{ y: 0, scale: 1 }}
                  exit={shouldReduceMotion ? undefined : { y: -12, scale: 0.6 }}
                  transition={COUNT_TRANSITION}
                >
                  {displayedCount}
                </S.Count>
              </AnimatePresence>
            </S.CountClip>
          </S.Bubble>
        </S.Badge>
      )}
    </AnimatePresence>
  )
}
